import { getScopedClient } from './supabase';

export interface LaunchConfig {
  title: string;
  subtitle: string;
  registration_link: string;
  webinar_date: string;
  webinar_time: string;
  landing_page_theme: string;
  presenter_image: string;
  launch_status: string;
  pipeline_id: string;
  pipeline_stage_id: string;
  zoom_join_link: string;
}

export async function getLaunchConfig(webinarId: string): Promise<LaunchConfig | null> {
  const client = getScopedClient();
  const { data, error } = await client
    .from('webinars')
    .select(
      'title, subtitle, registration_link, webinar_date, webinar_time, landing_page_theme, presenter_image, launch_status, pipeline_id, pipeline_stage_id, zoom_join_link'
    )
    .eq('id', webinarId)
    .single();

  if (error || !data) return null;

  return {
    title: data.title || '',
    subtitle: data.subtitle || '',
    registration_link: data.registration_link || '',
    webinar_date: data.webinar_date || '',
    webinar_time: data.webinar_time || '',
    landing_page_theme: data.landing_page_theme || '',
    presenter_image: data.presenter_image || '',
    launch_status: data.launch_status || 'draft',
    pipeline_id: data.pipeline_id || '',
    pipeline_stage_id: data.pipeline_stage_id || '',
    zoom_join_link: data.zoom_join_link || '',
  };
}

export async function saveLaunchConfig(
  webinarId: string,
  config: Partial<LaunchConfig>
): Promise<void> {
  const client = getScopedClient();
  const { error } = await client
    .from('webinars')
    .update(config)
    .eq('id', webinarId);

  if (error) {
    throw new Error(`Failed to save launch config: ${error.message}`);
  }
}

export function calculateEmailDates(webinarDate: Date) {
  const day = 86400000;
  return {
    // 8 marketing emails over 2-week run-up (webinar is always Thursday)
    marketing1: new Date(webinarDate.getTime() - 10 * day), // Mon, Week 1 — "The Mistake Hook"
    marketing2: new Date(webinarDate.getTime() - 8 * day),  // Wed, Week 1 — "The Origin / Trust"
    marketing3: new Date(webinarDate.getTime() - 6 * day),  // Fri, Week 1 — "The Logic / Villain"
    marketing4: new Date(webinarDate.getTime() - 2 * day),  // Tue, Week 2 — "The Goodie Bag"
    marketing5: new Date(webinarDate.getTime() - 1 * day),  // Wed, Week 2 — "Behind the Scenes Tease"
    marketing6: new Date(webinarDate.getTime()),             // Thu AM — "The Agenda"
    marketing7: new Date(webinarDate.getTime()),             // Thu, 1hr before — "Pure Urgency"
    marketing8: new Date(webinarDate.getTime()),             // Thu, LIVE NOW — "The FOMO"
    // 3 follow-up emails after the webinar
    followup1: new Date(webinarDate.getTime()),              // Thu night, 4hrs post — "Recap & Shift"
    followup2: new Date(webinarDate.getTime() + 1 * day),   // Fri AM — "The Case Study"
    followup3: new Date(webinarDate.getTime() + 3 * day),   // Mon AM — "The Spear"
  };
}

export const LANDING_PAGE_THEMES = [
  { value: 'professional-authority', label: 'Professional Authority' },
  { value: 'warm-personal', label: 'Warm & Personal' },
  { value: 'bold-direct', label: 'Bold & Direct' },
  { value: 'educational-expert', label: 'Educational Expert' },
  { value: 'community-builder', label: 'Community Builder' },
  { value: 'premium-exclusive', label: 'Premium Exclusive' },
] as const;

export const TIME_OPTIONS: string[] = [];
for (let h = 8; h <= 21; h++) {
  for (const m of [0, 30]) {
    if (h === 21 && m === 30) continue;
    const hour12 = h > 12 ? h - 12 : h === 0 ? 12 : h;
    const ampm = h >= 12 ? 'PM' : 'AM';
    const min = m.toString().padStart(2, '0');
    TIME_OPTIONS.push(`${hour12}:${min} ${ampm}`);
  }
}
